import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, FileCheck, Clock, ChevronRight, Globe, Shield, CheckCircle } from "lucide-react";
import Footer from "@/components/Footer";

const LandingPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: MapPin,
      title: "50+ Countries",
      description: "Discover visa options across Europe, Americas, Asia, and Oceania"
    },
    {
      icon: FileCheck,
      title: "Smart Eligibility",
      description: "Assessment based on your income, savings, and employment"
    },
    {
      icon: Clock,
      title: "Timeline Tracking",
      description: "Step-by-step checklists with realistic processing estimates"
    },
    {
      icon: Shield,
      title: "Document Vault",
      description: "Keep your passport scans, proofs, and forms together in one secure place"
    }
  ];

  const destinations = [
    { name: "Portugal", emoji: "🇵🇹", image: "https://images.unsplash.com/photo-1643757788305-6aa601d66a4f?w=400" },
    { name: "Mexico", emoji: "🇲🇽", image: "https://images.unsplash.com/photo-1759255262693-a832872cfea4?w=400" },
    { name: "Spain", emoji: "🇪🇸", image: "https://images.unsplash.com/photo-1652946337021-2ba5ca96c36c?w=400" },
    { name: "Thailand", emoji: "🇹🇭", image: "https://images.unsplash.com/photo-1528181304800-259b08848526?w=400" }
  ];

  const steps = [
    { step: 1, title: "Tell us about yourself", desc: "Quick questionnaire about your situation" },
    { step: 2, title: "See where you qualify", desc: "Instant results with clear explanations" },
    { step: 3, title: "Track your progress", desc: "Organized checklists for each destination" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="h-6 w-6 text-primary" />
            <span className="font-bold text-lg font-['Manrope']">Nomad Roadmap</span>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/login")} className="hidden sm:flex">Sign In</Button>
            <Button onClick={() => navigate("/register")} className="bg-primary hover:bg-primary/90 rounded-full px-6">Get Started</Button>
          </div>
        </div>
      </nav>

      <section className="pt-24 pb-16 md:pt-32 md:pb-24 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-secondary/50 text-secondary-foreground px-4 py-2 rounded-full text-sm font-medium">
                <Shield className="h-4 w-4" />
                For U.S. Citizens Moving Abroad
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground font-['Manrope'] leading-tight">
                Stop guessing.<br />
                <span className="text-primary">Start planning.</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-lg">
                Find out which countries you qualify for, understand your visa options, and organize your entire application—all in one place.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" onClick={() => navigate("/register")} className="bg-primary hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-medium shadow-lg">
                  Check Your Eligibility
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate("/pricing")} className="rounded-full px-8 py-6 text-lg border-2">View Pricing</Button>
              </div>
              <div className="flex items-center gap-6 text-sm text-muted-foreground pt-4">
                <div className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-primary" />No legal advice</div>
                <div className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-primary" />Planning only</div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img src="https://images.unsplash.com/photo-1650113794972-56031832c0db?w=800" alt="Traveler at airport" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default LandingPage;
